import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AUTH_SERVICE_FE_CONFIG } from './auth-service-fe.config';
import type {
  GetUsersResponse,
  LoginResponse,
  SignUpRequest,
  SignUpResponse,
  UpdateUserRequest,
  DeleteUserRequest,
  DeleteUsersRequest,
  AcceptedResponse,
  ChangePasswordResponse,
} from '../interfaces/auth-service-fe.dto';
import type { FilterMatchMode } from '../../log-service-fe/src/log-admin.service';

export interface GetUsersParams {
  limit?: number;
  offset?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  from?: string;
  to?: string;
  search?: string;
  searchMode?: FilterMatchMode;
  status?: string;
  statusMode?: FilterMatchMode;
  subjectType?: string;
  subjectTypeMode?: FilterMatchMode;
  subscriptionLevel?: string;
  subscriptionLevelMode?: FilterMatchMode;
}

@Injectable({
  providedIn: 'root',
})
export class AuthServiceFe {
  private readonly http = inject(HttpClient);
  private readonly config = inject(AUTH_SERVICE_FE_CONFIG, { optional: true });

  logIn(accessToken: string) {
    return this.http.get<LoginResponse>(`${this.getEndpoint()}/get-me`, {
      headers: this.createAuthHeaders(accessToken),
    });
  }

  signUp(payload: SignUpRequest, accessToken: string) {
    return this.http.post<SignUpResponse>(
      `${this.getEndpoint()}/create-user`,
      payload,
      {
        headers: this.createAuthHeaders(accessToken),
      },
    );
  }

  getUsers(accessToken: string, params?: GetUsersParams) {
    let httpParams = new HttpParams();
    if (params) {
      if (params.limit != null)
        httpParams = httpParams.set('limit', params.limit);
      if (params.offset != null)
        httpParams = httpParams.set('offset', params.offset);
      if (params.sort) httpParams = httpParams.set('sort', params.sort);
      if (params.order) httpParams = httpParams.set('order', params.order);
      if (params.from) httpParams = httpParams.set('from', params.from);
      if (params.to) httpParams = httpParams.set('to', params.to);
      if (params.search) httpParams = httpParams.set('search', params.search);
      if (params.searchMode)
        httpParams = httpParams.set('search_mode', params.searchMode);
      if (params.status) httpParams = httpParams.set('status', params.status);
      if (params.statusMode)
        httpParams = httpParams.set('status_mode', params.statusMode);
      if (params.subjectType)
        httpParams = httpParams.set('subject_type', params.subjectType);
      if (params.subjectTypeMode)
        httpParams = httpParams.set(
          'subject_type_mode',
          params.subjectTypeMode,
        );
      if (params.subscriptionLevel)
        httpParams = httpParams.set(
          'subscription_level',
          params.subscriptionLevel,
        );
      if (params.subscriptionLevelMode)
        httpParams = httpParams.set(
          'subscription_level_mode',
          params.subscriptionLevelMode,
        );
    }
    return this.http.get<GetUsersResponse>(`${this.getEndpoint()}/get-users`, {
      headers: this.createAuthHeaders(accessToken),
      params: httpParams,
    });
  }

  updateUser(payload: UpdateUserRequest, accessToken: string) {
    return this.http.post<AcceptedResponse>(
      `${this.getEndpoint()}/update-user`,
      payload,
      {
        headers: this.createAuthHeaders(accessToken),
      },
    );
  }

  deleteUser(payload: DeleteUserRequest, accessToken: string) {
    return this.http.post<AcceptedResponse>(
      `${this.getEndpoint()}/delete-user`,
      payload,
      {
        headers: this.createAuthHeaders(accessToken),
      },
    );
  }

  deleteUsers(payload: DeleteUsersRequest, accessToken: string) {
    return this.http.post<AcceptedResponse>(
      `${this.getEndpoint()}/delete-users`,
      payload,
      {
        headers: this.createAuthHeaders(accessToken),
      },
    );
  }

  changePassword(newPassword: string, accessToken: string) {
    return this.http.post<ChangePasswordResponse>(
      `${this.getEndpoint()}/change-password`,
      { newPassword },
      {
        headers: this.createAuthHeaders(accessToken),
      },
    );
  }

  private getEndpoint(): string {
    return this.config?.endpoint ?? '/user-api';
  }

  private createAuthHeaders(accessToken: string): HttpHeaders {
    return new HttpHeaders({
      authorization: `Bearer ${accessToken}`,
    });
  }
}
